import { Badge, Button, Modal, ModalBody, ModalHeader } from 'reactstrap';
import { Answer } from '../types';
import { RetrievedChunksDisplay } from './RetrievedChunksDisplay';
import type { DocumentMetadata } from '@api/ai-supervisor';
import { MarkdownRenderer } from './MarkdownRenderer';

interface QuestionDetailDialogProps {
  answer: Answer | null;
  onClose: () => void;
  documentMetadata?: Record<string, DocumentMetadata>;
}

export function QuestionDetailDialog({ answer, onClose, documentMetadata }: QuestionDetailDialogProps) {
  if (!answer) return null;

  // Semaphore coloring for confidence scores
  const getConfidenceColor = (confidence: number | undefined) => {
    if (confidence === undefined) return { bg: 'secondary', text: 'secondary', border: 'secondary' };
    if (confidence >= 90) return { bg: 'success', text: 'success', border: 'success' };
    if (confidence >= 70) return { bg: 'warning', text: 'warning', border: 'warning' };
    return { bg: 'danger', text: 'danger', border: 'danger' };
  };

  const handleApprove = (_id: string) => {
    alert('Not implemented');
  };

  const confidenceColors = getConfidenceColor(answer.confidence);

  return (
    <Modal isOpen={!!answer} toggle={onClose} data-slot='question-detail-dialog' size='xl'>
      <ModalHeader toggle={onClose}>
        <div className='px-2'>Answer Details</div>
      </ModalHeader>

      <ModalBody className='d-flex flex-column'>
        {/* Fixed Header Section - Always visible */}
        <div className='d-flex gap-2'>
          <div className='flex-fill px-2'>
            {/* Question */}
            <div className='mb-3'>
              <h4 className='mb-0'>{answer.questionName}</h4>
              <h5 className='mb-0'>{answer.questionValueLabel}</h5>
            </div>

            {/* Answer */}
            <div className='mb-3 border border-ThemeNeutralsLight rounded p-3 answer-content'>
              {!answer.answer && answer.status !== 'completed' ? (
                <div className='text-muted text-center py-4'>
                  <i className='fas fa-spinner fa-spin me-2' />
                  Generating answer...
                </div>
              ) : (
                (typeof answer.answer === 'string' ?
                  <MarkdownRenderer content={answer.answer ?? 'No answer available'} />
                  :
                  <div>
                    {answer.answer}
                  </div>
                )
              )}
            </div>
          </div>
          <div className='d-flex flex-column gap-2'>
            <div className='d-flex justify-content-end gap-2'>
              <Button color='primary' className='text-nowrap' onClick={() => handleApprove(answer.id)}>
                Approve Answer
              </Button>
            </div>
            <div className='text-nowrap d-flex flex-column background-ThemeBgToolbar rounded p-2'>

              <div className={`d-flex text-strong text-${confidenceColors.text} p-2`}>
                <div><i className={`fal fa-chart-line fa-2x text-${confidenceColors.text} me-2`} /></div>
                <div className='text-xl flex-fill text-right w-100'>{answer.confidence ?? '-'}%</div>
              </div>

              <div className='d-flex text-strong text-primary p-2'>
                <div><i className='fal fa-quote-right fa-2x text-primary me-2' /></div>
                <div className='text-xl flex-fill text-right w-100'>{answer.citationsCount}</div>
              </div>

              <Badge
                className='text-md w-100'
                color={
                  answer.status === 'completed' ? 'success' : answer.status === 'failed' ? 'danger' : 'secondary'
                }
              >
                {answer.status ? answer.status.charAt(0).toUpperCase() + answer.status.slice(1) : ''}
              </Badge>

              {answer.stage && (
                <>
                  <div className='text-secondary p-2'>
                    <i className='fal fa-layer-group fa-2x text-secondary me-2' />
                    <span className='text-xl'>{answer.stage}</span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Error display */}
        {answer.error && (
          <div className='text-lg text-danger p-2'>
            <i className='fal fa-exclamation-triangle text-danger' />
            {typeof answer.error === 'string' ? (
              <div className='d-flex align-items-center gap-2'>
                <i className='fal fa-exclamation-triangle' />
                <span className='small'>Error: {answer.error}</span>
              </div>
            ) : (
              <div>
                <div className='d-flex align-items-start gap-2'>
                  <i className='fal fa-exclamation-triangle' />
                  <div className='flex-fill'>
                    <div className='small fw-bold mb-1'>{answer.error.message}</div>
                    <div className='d-flex align-items-center gap-3 small text-muted'>
                      <span>Code: {answer.error.code}</span>
                      <span>•</span>
                      <span>Agent: {answer.error.agent}</span>
                      {answer.error.retryable && (
                        <>
                          <span>•</span>
                          <span className='text-warning'>
                            <i className='fal fa-sync-alt me-1' />
                            Retryable
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        <div className='mt-3' style={{ minHeight: 0, marginLeft: '-1.6rem', marginRight: '-1.6rem' }}>
          {answer.retrievedChunks && answer.retrievedChunks.length > 0 ? (
            <RetrievedChunksDisplay
              retrievedChunks={answer.retrievedChunks}
              citations={answer.citations}
              documentMetadata={documentMetadata}
            />
          ) : (
            <div className='text-muted text-center py-5'>
              <i className='fal fa-file-alt fa-3x mb-3 d-block' />
              <p>No citations available</p>
            </div>
          )}
        </div>
      </ModalBody>
    </Modal>
  );
}
